/* Split out of the old monolithic Settings.js so each ตั้งค่า page can be
   rebuilt against its own capture without colliding with the others. */
import { useState } from 'react';
import { settingsApi } from '../../api';
import FormModal from '../../components/FormModal';
import { RowMenu } from '../../components/SettingGroups';
import { IconGlobe } from '../../components/icons';
import { Modal, useLoader } from '../../components/common';
import { SettingsPage, Panel } from './shell';

/* The อาชีพ tab's icon. Kept local because this is the only screen that shows
   it; drawn to icons.js's conventions (1.6px stroke on a 24-box). */
const IconBriefcase = ({ size = 17 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="7.5" width="18" height="12" rx="2" />
    <path d="M9 7.5V5.8a1.3 1.3 0 0 1 1.3-1.3h3.4A1.3 1.3 0 0 1 15 5.8v1.7" />
    <path d="M3 12.5h18" />
  </svg>
);

/**
 * The two tabs are the same screen twice over; only the list and the way a row
 * is identified differ. สัญชาติ has no id column at all — its primary key is
 * the two-letter `code` — so every call has to be told which one to send.
 */
const TABS = {
  nationality: {
    noun: 'สัญชาติ',
    entity: 'nationalities',
    icon: IconGlobe,
    rows: (d) => d.nationalities,
    id: (r) => r.code,
  },
  occupation: {
    noun: 'อาชีพ',
    entity: 'occupations',
    icon: IconBriefcase,
    rows: (d) => d.occupations,
    id: (r) => r.id,
  },
};

/* 15px, measured: their tab icon sits in a 31x23 chip, which is a 15px glyph
   plus the chip's own 4px/8px. At 17 the chip grows and the tab with it, which
   pushed the whole list 2px down the page. */
const TAB_LIST = Object.entries(TABS).map(([key, t]) => ({
  key,
  label: (
    <span className="inline-flex items-center gap-2">
      <t.icon size={15} />
      {t.noun}
    </span>
  ),
}));

/** ไทย (Thai) — Thai name with the English one in parentheses. */
const label = (r) => (r.name_th ? `${r.name_th} (${r.name_en})` : r.name_en);

/**
 * สัญชาติ and อาชีพ are the only settings rows that are genuinely erased:
 * their tables carry no is_active, so there is nothing to retire them into.
 * The server refuses while a customer still points at the row, and that
 * refusal has nowhere else to surface — hence a dialog rather than a menu item
 * that silently does nothing.
 */
function RemoveDialog({ noun, name, onConfirm, onClose }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`ลบ${noun}`}
      onClose={onClose}
      footer={(
        <>
          <button className="btn-outline" onClick={onClose} disabled={busy}>ยกเลิก</button>
          <button className="btn-danger" onClick={go} disabled={busy}>
            {busy ? 'กำลังลบ…' : 'ลบ'}
          </button>
        </>
      )}
    >
      {error && <div className="login-error">{error}</div>}
      <p style={{ margin: 0, lineHeight: 1.8 }}>
        ต้องการลบ <strong>{name}</strong> ใช่หรือไม่?
      </p>
    </Modal>
  );
}

export function SettingCustomer() {
  const [tab, setTab] = useState('nationality');
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const refs = useLoader(() => settingsApi.customerRefs(), []);

  const cfg = TABS[tab];

  /* Measured on their /setting-customer: a flat white box — 56px tall, a 16px
     inset, an 8px radius — with no border and no shadow. `.card` gave ours
     both, which is what made a สัญชาติ read as a raised card. */
  const band = (r) => (
    <div
      key={cfg.id(r)}
      className="bg-white rounded-[8px] flex items-center justify-between gap-2 p-4 h-[56px] text-[16px]"
    >
      <span className="font-normal">{label(r)}</span>
      {tab !== 'nationality' && (
        <RowMenu
          items={[
            { label: 'แก้ไข', onClick: () => setEditing(r) },
            { label: 'ลบ', danger: true, onClick: () => setRemoving(r) },
          ]}
        />
      )}
    </div>
  );

  return (
    <SettingsPage
      tab={tab}
      onTab={(next) => {
        setTab(next);
        setQuery('');
        setCreating(false);
        setEditing(null);
        setRemoving(null);
      }}
      action={tab === 'occupation' ? 'สร้างอาชีพ' : undefined}
      onAction={tab === 'occupation' ? () => setCreating(true) : undefined}
      tabs={TAB_LIST}
      search={tab === 'nationality' ? (
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="ค้นหาสัญชาติ"
          aria-label="ค้นหาสัญชาติ"
        />
      ) : undefined}
    >
      <Panel loader={refs}>
        {(d) => {
          const allRows = cfg.rows(d);
          const needle = query.trim().toLocaleLowerCase();
          const rows = tab === 'nationality' && needle
            ? allRows.filter((row) => (
                row.code.toLocaleLowerCase().includes(needle)
                || row.name_en.toLocaleLowerCase().includes(needle)
                || (row.name_th || '').toLocaleLowerCase().includes(needle)
              ))
            : allRows;
          return (
            <>
              {/* Two columns 16px apart, measured: their grid is 1328px of
                  656px bands with 16px between them both ways. */}
              {rows.length ? (
                <div className="grid grid-cols-2 gap-4">{rows.map(band)}</div>
              ) : (
                <div className="bg-white rounded-[8px] px-4 py-4 text-sm text-gray-400">
                  ยังไม่มี{cfg.noun}
                </div>
              )}

              {tab === 'occupation' && (
                <>
                  <div className="setting-section">{cfg.noun}ที่ปิดใช้งาน</div>
                  <div className="bg-white rounded-[8px] px-4 py-4 text-sm text-gray-400">
                    ไม่มี{cfg.noun}ที่ปิดใช้งาน
                  </div>
                </>
              )}
            </>
          );
        }}
      </Panel>

      {creating && tab === 'occupation' && (
        <FormModal
          title="สร้างอาชีพ"
          fields={[
            {
              name: 'code',
              label: 'รหัส',
              required: true,
            },
            { name: 'nameEn', label: 'ชื่อ (อังกฤษ)', required: true },
            { name: 'nameTh', label: 'ชื่อ (ไทย)' },
          ]}
          onSubmit={async (v) => {
            const payload = { code: v.code, nameEn: v.nameEn, nameTh: v.nameTh || null };
            await settingsApi.createOccupation(payload);
            refs.reload();
            setCreating(false);
          }}
          onClose={() => setCreating(false)}
        />
      )}

      {editing && (
        <FormModal
          title={`แก้ไข${cfg.noun} — ${label(editing)}`}
          fields={[
            { name: 'nameEn', label: 'ชื่อ (อังกฤษ)', required: true },
            { name: 'nameTh', label: 'ชื่อ (ไทย)' },
          ]}
          initial={{ nameEn: editing.name_en, nameTh: editing.name_th || '' }}
          onSubmit={async (v) => {
            await settingsApi.update(cfg.entity, cfg.id(editing), {
              nameEn: v.nameEn,
              nameTh: v.nameTh || null,
            });
            refs.reload();
            setEditing(null);
          }}
          onClose={() => setEditing(null)}
        />
      )}

      {removing && (
        <RemoveDialog
          noun={cfg.noun}
          name={label(removing)}
          onConfirm={async () => {
            await settingsApi.remove(cfg.entity, cfg.id(removing));
            refs.reload();
            setRemoving(null);
          }}
          onClose={() => setRemoving(null)}
        />
      )}
    </SettingsPage>
  );
}
